import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';

import myShopClient from '@services/nearlive';

// Throttle the login-sync to once per user per 24h via localStorage, so a
// returning user doesn't re-hit /api/loops/sync-me on every dashboard mount.
const TTL_MS = 24 * 60 * 60 * 1000;
const storageKey = (userId) => `unify:loops-synced:${userId}`;

const syncedRecently = (userId) => {
	try {
		const at = Number(window.localStorage.getItem(storageKey(userId)));
		return Number.isFinite(at) && at > 0 && Date.now() - at < TTL_MS;
	} catch {
		return false; // private mode / disabled storage → just sync
	}
};

const markSynced = (userId) => {
	try {
		window.localStorage.setItem(storageKey(userId), String(Date.now()));
	} catch {
		// A failed write just means we may sync again on the next mount — harmless.
	}
};

/**
 * Fire-and-forget login-sync (NEA-5897): once per session — per Clerk user,
 * throttled to 24h via localStorage — POST `/api/loops/sync-me` so the signed-in
 * user has a fully-populated Loops contact even if they have never clicked the
 * subscribe CTA or triggered a Stripe lifecycle event.
 *
 * Skipped for impersonated HQ sessions (the endpoint rejects them too, but there
 * is no reason to fire). Best-effort: any failure is swallowed and the
 * localStorage stamp is written only on success, so a transient failure retries
 * on the next mount.
 */
export function useEnsureLoopsContact() {
	const { isLoaded, isSignedIn, userId, sessionClaims } = useAuth();
	const firedRef = useRef(false);
	const isImpersonating = Boolean(sessionClaims?.act);

	useEffect(() => {
		if (!isLoaded || !isSignedIn || !userId || isImpersonating) return;
		if (firedRef.current || syncedRecently(userId)) return;
		firedRef.current = true;

		(async () => {
			try {
				const client = await myShopClient();
				await client.post('/api/loops/sync-me');
				markSynced(userId);
			} catch {
				firedRef.current = false; // allow a retry on a later mount
			}
		})();
	}, [isLoaded, isSignedIn, userId, isImpersonating]);
}
